import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getPlanList } from "../api/plan";
import { generateTokenByApiKey } from "../api/login";

const usePlans = (tenantId) => {
  const dispatch = useDispatch();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0); 

  const fetchPlans = useCallback(() => {
    if (!tenantId) {
      setPlans([]);
      setLoading(false);
      setError("Missing tenantId - cannot load plans.");
      return;
    }

    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);

    let keys;
    try {
      keys = JSON.parse(process.env.REACT_APP_API_KEYS || "[]");
    } catch {
      keys = [];
    }
    const key = keys.find((f) => f.tenantId === tenantId);

    if (!key) {
      setPlans([]);
      setLoading(false);
      setError("No API key configured for this tenant.");
      return;
    }

    dispatch(
      generateTokenByApiKey(
        {},
        "zealthix",
        {
          "x-tenant-id": key.tenantId,
          "X-Api-Key": key.key,
          "Content-Type": "application/json",
        },
        (t) => {
          if (currentRequest !== requestId.current) return;

          if (!t?.access_token) {
            setPlans([]);
            setLoading(false);
            setError("Could not authenticate to load plans.");
            return;
          }

          dispatch(
            getPlanList({ count: true, tenantId }, t.access_token, (res) => {
              if (currentRequest !== requestId.current) return;

              const data = Array.isArray(res)
                ? res
                : Array.isArray(res?.data)
                ? res.data
                : [];

              setPlans(data);
              setLoading(false);
              setError(null);
            })
          );

          setTimeout(() => {
            if (currentRequest === requestId.current) {
              setLoading((prevLoading) => {
                if (prevLoading) {
                  setError("Couldn't load plans right now. Please try again.");
                  return false;
                }
                return prevLoading;
              });
            }
          }, 15000);
        }
      )
    );
  }, [dispatch, tenantId]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return { plans, loading, error, refetch: fetchPlans };
};

export default usePlans;
